// Fachsprache und Aufrufe der Kontaktwegpflege (ST-PTY-002, erster Schnitt).
//
// REINES MODELL, kein React, kein DOM -- dieselbe Trennung wie in parties.ts
// und quick-capture.ts: was hier steht, ist ohne Browser pruefbar.
//
// DAS WORT "USAGE" KOMMT AUF DEM BILDSCHIRM NICHT VOR, und "Party" auch nicht.
// Auf dem Bildschirm heisst das eine "Verwendung eines Kontaktwegs" und das
// andere "Kontakt" (NFR-UX-003, CM-UX-010).

import type { PlainMessage } from './access.ts';
import { sendCommand, type CommandMessages, type CommandOutcome, type SendLike } from './command-client.ts';

export type { SendLike } from './command-client.ts';

export type ManagedChannel = 'email' | 'phone';

/**
 * Was die Pflege als Zeile zeigt: dazu die Website. Sie laesst sich nur
 * beenden und bevorzugen; Anlegen und Korrigieren brauchen eine Formregel,
 * die der Server nur fuer E-Mail und Telefon kennt (MANAGED_CHANNELS).
 */
export type ListedChannel = ManagedChannel | 'web';

/** Unter der Liste der Pflege, solange es eine Website gibt oder gab: hier laesst sie sich weder neu eintragen noch ersetzen. */
export const WEBSITE_NICHT_ERSETZBAR = 'Eine Website kannst du hier noch nicht neu eintragen oder ändern.';

/**
 * Die Endgruende, die dieser Bildschirm anbietet.
 *
 * SIE SIND EINE TEILMENGE VON `party.end_reason` UND NICHT DIE GANZE LISTE.
 * ADR-0022 Punkt 1 haengt an drei der fuenf Codes eine Beleg- oder
 * Quellenpflicht; einen davon anzubieten waere eine Auswahl, die der Server
 * anschliessend mit 422 ablehnt -- also eine Attrappe (ADR-0002). Angeboten
 * wird genau, was heute durchgeht.
 *
 * DIE BINDUNG IST EINE PROBE UND KEIN VERSPRECHEN: apps/web/test/
 * contact-points.test.ts liest `db/migrations/0032` und prueft, dass jeder
 * Code hier dort mit `requires_evidence = false` und `requires_source = false`
 * steht. Waechst die Werteliste, faellt die Probe -- nicht der Bildschirm.
 *
 * Die Reihenfolge ist die Haeufigkeit im Alltag und nicht die der Migration:
 * ein Umzug oder eine neue Nummer ist der Regelfall, ein entfallener Zweck die
 * Ausnahme.
 */
export const OFFERED_END_REASONS: readonly { readonly code: string; readonly label: string; readonly hint: string }[] =
  Object.freeze([
    Object.freeze({
      code: 'superseded',
      label: 'Es gibt einen Nachfolger',
      hint: 'Umzug, neue Adresse, neue Nummer. Der Kontakt ist weiterhin erreichbar — nur anders.',
    }),
    Object.freeze({
      code: 'purpose_ended',
      label: 'Der Zweck ist entfallen',
      hint: 'Vertrag oder Rolle sind beendet. Der Kontaktweg darf hier nicht mehr verwendet werden.',
    }),
  ]);

/**
 * Dieselben Codes fuer die Website, mit eigenen Saetzen (DESIGN B11).
 * "Umzug, neue Nummer" passt nicht zu einer Website, und eine neue laesst sich
 * hier nicht eintragen -- das sagt der Hinweis. Codes und Reihenfolge sind die
 * von OFFERED_END_REASONS, damit der vorgewaehlte erste Grund fuer jede Zeile
 * gilt; contact-points.test.ts misst das. Die Historie bleibt kanalgleich.
 */
export const WEBSITE_END_REASONS: readonly { readonly code: string; readonly label: string; readonly hint: string }[] =
  Object.freeze([
    Object.freeze({
      code: 'superseded',
      label: 'Die Website hat sich geändert',
      hint: 'Neue Adresse oder neuer Auftritt. Die neue Website kannst du hier noch nicht eintragen.',
    }),
    Object.freeze({
      code: 'purpose_ended',
      label: 'Die Website gibt es nicht mehr',
      hint: 'Der Auftritt ist eingestellt oder gehört nicht mehr zu diesem Kontakt.',
    }),
  ]);

/**
 * Fachbegriffe der Endgruende -- fuer die HISTORIE.
 *
 * Sie ist weiter als die Auswahl oben, und das ist Absicht: eine Verwendung
 * kann aus einem Grund beendet worden sein, den dieser Bildschirm nicht
 * anbietet (etwa durch einen spaeteren Importlauf). Ein unuebersetzter Code in
 * der Historie waere ein Technikbegriff auf dem Bildschirm; ein leeres Feld
 * waere schlimmer, weil es wie "kein Grund" aussieht.
 */
export const END_REASON_LABELS: Readonly<Record<string, string>> = Object.freeze({
  superseded: 'Nachfolger vorhanden',
  purpose_ended: 'Zweck entfallen',
  no_longer_reachable: 'Nicht mehr erreichbar',
  party_status_change: 'Personen- oder Organisationsstatus geändert',
  subject_request: 'Auf Äußerung der betroffenen Person',
});

/** Was in der Historie steht, wenn eine Zeile vor ADR-0022 beendet wurde. */
export const END_REASON_MISSING = 'ohne festgehaltenen Grund';

export function endReasonLabel(code: string | undefined): string {
  if (code === undefined || code.length === 0) {
    return END_REASON_MISSING;
  }
  return END_REASON_LABELS[code] ?? code;
}

export const CHANNEL_LABELS: Readonly<Record<string, string>> = Object.freeze({
  email: 'E-Mail',
  phone: 'Telefon',
  web: 'Website',
});

export function channelLabel(code: string): string {
  return CHANNEL_LABELS[code] ?? code;
}

/**
 * Der Ausgang eines Befehls, in der Sprache des Bildschirms.
 *
 * FUENF AUSGAENGE UND NICHT ZWEI, weil sie fuenf verschiedene naechste
 * Schritte tragen (NFR-UX-005). `conflict` ist der teuerste: er ist der
 * einzige, nach dem die Nutzerin NICHT einfach wiederholen darf, sondern
 * zuerst sehen muss, was inzwischen gilt.
 */
export type CommandResult = CommandOutcome<{ readonly channel: string; readonly lockVersion: number }>;

/**
 * Die Zwecke, die "Auch verwenden als" anbietet -- dieselben vier wie der
 * Server (ADDITIONAL_CONTACT_POINT_USAGE_CODES), ohne privat. Die
 * Beschriftung ist die der Liste (parties.ts, USAGE_LABELS); der Hinweis
 * sagt, wofuer der Zweck steht, damit "geschaeftlich" und "Assistenz"
 * unterscheidbar sind.
 */
export const OFFERED_CONTACT_POINT_USAGES: readonly { readonly code: string; readonly label: string; readonly hint: string }[] =
  Object.freeze([
    Object.freeze({ code: 'business', label: 'geschäftlich', hint: 'Der Kontaktweg, unter dem dieser Kontakt selbst geschäftlich erreichbar ist.' }),
    Object.freeze({ code: 'assistant', label: 'Assistenz', hint: 'Erreicht die Assistenz oder das Sekretariat dieser Person, nicht sie selbst.' }),
    Object.freeze({ code: 'billing', label: 'Rechnung', hint: 'Für Rechnungen und Buchhaltung.' }),
    Object.freeze({ code: 'reception', label: 'Zentrale', hint: 'Erreicht die Zentrale oder den Empfang, nicht eine einzelne Person.' }),
  ]);

function offeredLabel(usageCode: string): string {
  return OFFERED_CONTACT_POINT_USAGES.find((usage) => usage.code === usageCode)?.label ?? usageCode;
}

/**
 * Ein Kontaktweg mit seinen offenen Zwecken. Die Liste des Servers fuehrt je
 * Verwendung eine Zeile; gruppiert wird nach dem MECHANISMUS
 * (`contactPointId`) und nie nach dem Wert -- zwei gleich geschriebene
 * Adressen sind ohne Entscheidung nicht derselbe Kontaktweg.
 */
export interface ContactPointGroup<T extends { readonly contactPointId: string; readonly value: string; readonly verificationStatus: string }> {
  readonly contactPointId: string;
  readonly value: string;
  readonly verificationStatus: string;
  /** Die erste Verwendung in der Reihenfolge des Servers. */
  readonly first: T;
  readonly usages: readonly T[];
}

export function groupContactPoints<T extends { readonly contactPointId: string; readonly value: string; readonly verificationStatus: string }>(
  contacts: readonly T[],
): readonly ContactPointGroup<T>[] {
  const groups = new Map<string, T[]>();
  for (const contact of contacts) {
    const usages = groups.get(contact.contactPointId);
    if (usages === undefined) {
      groups.set(contact.contactPointId, [contact]);
    } else {
      usages.push(contact);
    }
  }
  return [...groups.values()].map((usages) => {
    const first = usages[0] as T;
    return { contactPointId: first.contactPointId, value: first.value, verificationStatus: first.verificationStatus, first, usages };
  });
}

/** Der Zweck ist an diesem Kontaktweg schon eingetragen; doppelt wird er nicht angelegt. */
export function duplicateContactPointUsageMessage(usageCode: string): PlainMessage {
  return Object.freeze({
    headline: `Dieser Kontaktweg ist schon als „${offeredLabel(usageCode)}“ verwendet.`,
    explanation: 'Der Zweck wurde inzwischen eingetragen. Ein zweites Mal legt LIAISON ihn nicht an.',
    nextStep: 'Lade die Kontaktwege neu; der Zweck steht dann am Kontaktweg.',
    retryable: false,
  });
}

export function usageConfirmation(channel: string, usageCode: string): string {
  return `${channelLabel(channel)} gilt jetzt auch für den Zweck „${offeredLabel(usageCode)}“. Jeder Zweck lässt sich später einzeln wieder aufgeben.`;
}

/**
 * Der Satz, den ein Konflikt bekommt.
 *
 * Er steht HIER und nicht in access.ts, weil er kein Grundcode der
 * Zugriffskontrolle ist, sondern eine fachliche Lage dieses Bildschirms. Und
 * er nennt ausdruecklich den WEG ZURUECK: neu laden, dann sehen, was gilt.
 * Ein "versuche es erneut" waere hier falsch -- ein zweiter Versuch mit
 * demselben veralteten Stand endet genauso.
 */
export const CONFLICT_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Jemand anderes war schneller.',
  explanation: 'Dieser Kontaktweg hat sich geändert, seit du diese Seite geöffnet hast. Deine Eingabe wurde nicht gespeichert.',
  nextStep: 'Lade die Kontaktwege neu und entscheide dann noch einmal.',
  retryable: false,
});

export const ALREADY_ENDED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Dieser Kontaktweg wird bereits nicht mehr verwendet.',
  explanation: 'Eine andere Person hat ihn inzwischen auf „Nicht mehr verwenden“ gesetzt. Ein zweites Mal geht das nicht.',
  nextStep: 'Lade die Kontaktwege neu; er steht dann unter „Frühere Kontaktwege“.',
  retryable: false,
  // Der gewollte Zustand gilt schon: ein Hinweis, kein Fehler.
  notice: true,
});

export const NOT_FOUND_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diesen Kontaktweg gibt es hier nicht.',
  // KEINE VERMUTUNG UEBER EINE BERECHTIGUNG. Der Server antwortet auf "gibt es
  // nicht" und "darfst du nicht sehen" mit demselben Ausgang, damit sich der
  // Bestand nicht abzaehlen laesst; die Oberflaeche darf ihn nicht aufloesen.
  explanation: 'Er wurde entfernt, oder die Adresse ist nicht mehr gültig.',
  nextStep: 'Geh zurück zum Kontakt und öffne die Kontaktwege dort erneut.',
  retryable: false,
});

export const END_REASON_UNAVAILABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Dieser Grund lässt sich hier nicht auswählen.',
  explanation: 'Für ihn verlangt LIAISON einen Nachweis oder eine benannte Quelle, und beides gibt es in dieser Ansicht noch nicht.',
  nextStep: 'Wähle einen der angebotenen Gründe.',
  retryable: false,
});

/**
 * Das Absenden ist fehlgeschlagen: die Verbindung riss ab, bevor eine Antwort
 * kam. Ob der Kontaktweg gespeichert wurde, weiss die Oberflaeche nicht -- und
 * sie muss es nicht wissen: die Wiederholung traegt dieselbe Kennung, und der
 * Server legt nichts doppelt an (ADR-0060). Deshalb `retryable: true`, und der
 * Satz sagt die Zusage in Worten.
 */
export const SUBMIT_FAILED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Das Absenden ist fehlgeschlagen.',
  explanation:
    'Die Verbindung ist abgebrochen, bevor LIAISON geantwortet hat. Ob der Kontaktweg schon gespeichert ist, ist offen. Deine Eingabe steht noch im Formular.',
  nextStep: 'Wähle „Erneut versuchen“. Ist der Kontaktweg schon angekommen, legt LIAISON ihn kein zweites Mal an.',
  retryable: true,
});

/**
 * Der erste Versuch kam mit ANDEREN Angaben an als der jetzige. Die jetzige
 * Eingabe ist nicht gespeichert und bleibt stehen; eine Wiederholung liefe in
 * dieselbe Ablehnung, deshalb "neu laden" und kein "erneut versuchen".
 */
export const REQUEST_REUSED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Dein erster Versuch ist mit anderen Angaben angekommen.',
  explanation: 'Deine jetzige Eingabe wurde nicht gespeichert. Sie steht noch im Formular.',
  nextStep: 'Lade die Kontaktwege neu und prüfe, ob der Kontaktweg schon da ist. Fehlt er, füge ihn danach hinzu.',
  retryable: false,
});

/**
 * Der erste Versuch IST angekommen, aber der Kontaktweg wurde seither
 * geaendert oder beendet. Der Bildschirm leert das Formular wie nach einem
 * Erfolg -- sonst legte der naechste Klick mit neuer Kennung einen zweiten an
 * (dieselbe Lehre wie beim Verlauf, Codex-Erstreview 15.09.2026).
 */
export const REQUEST_SUPERSEDED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Dieser Kontaktweg ist schon angelegt und wurde inzwischen geändert.',
  explanation:
    'Dein erster Versuch ist angekommen, das Formular ist deshalb wieder leer. Danach hat jemand den Kontaktweg geändert oder auf „Nicht mehr verwenden“ gesetzt; ein weiterer Versuch ändert daran nichts.',
  nextStep: 'Lade die Kontaktwege neu und sieh nach, was jetzt gilt.',
  retryable: false,
});

/**
 * "Auch verwenden als" legt keinen Kontaktweg an, sondern einen Zweck an einem
 * vorhandenen. Die Saetze des Hinzufuegens fragten sonst, ob "der Kontaktweg"
 * gespeichert ist, und versprachen eine Eingabe im Formular, die hier nicht
 * gemeint ist.
 */
export const USAGE_SUBMIT_FAILED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Das Absenden ist fehlgeschlagen.',
  explanation: 'Die Verbindung ist abgebrochen, bevor LIAISON geantwortet hat. Ob der Kontaktweg schon für den weiteren Zweck gilt, ist offen.',
  nextStep: 'Wähle „Erneut versuchen“. Ist der Zweck schon angekommen, trägt LIAISON ihn kein zweites Mal ein.',
  retryable: true,
});

/** Wie REQUEST_SUPERSEDED_MESSAGE, fuer einen weiteren Zweck; die Auswahl schliesst sich wie nach einem Erfolg. */
export const USAGE_SUPERSEDED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Dieser Zweck ist schon eingetragen und wurde inzwischen geändert.',
  explanation:
    'Dein erster Versuch ist angekommen, die Auswahl ist deshalb geschlossen. Danach hat jemand den Zweck geändert oder auf „Nicht mehr verwenden“ gesetzt; ein weiterer Versuch ändert daran nichts.',
  nextStep: 'Lade die Kontaktwege neu und sieh nach, was jetzt gilt.',
  retryable: false,
});

/** Nur "Auch verwenden als": der Kontakt nimmt keinen neuen Zweck mehr an. */
export const NOT_WRITABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'An diesem Kontakt lässt sich kein Zweck mehr eintragen.',
  explanation: 'Der Kontakt ist archiviert, anonymisiert oder mit einem anderen zusammengeführt.',
  nextStep: 'Geh zurück zum Kontakt und prüfe seinen Stand.',
  retryable: false,
});

/** Ein NEUER Kontaktweg: derselbe Grund wie oben, aber es geht um den Kontaktweg, nicht um einen Zweck (TM-017). */
export const CONTACT_POINT_NOT_ADDABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'An diesem Kontakt lässt sich kein Kontaktweg mehr eintragen.',
  explanation: 'Der Kontakt ist archiviert, anonymisiert oder mit einem anderen zusammengeführt.',
  nextStep: 'Geh zurück zum Kontakt und prüfe seinen Stand.',
  retryable: false,
});

/** Wie der Befehls-Client die Antworten der Kontaktwegpflege in Saetze uebersetzt. */
const COMMAND_MESSAGES: CommandMessages = Object.freeze({
  notFound: NOT_FOUND_MESSAGE,
  conflictFallback: CONFLICT_MESSAGE,
  conflicts: Object.freeze({
    contact_point_usage_already_ended: ALREADY_ENDED_MESSAGE,
    party_not_writable: NOT_WRITABLE_MESSAGE,
    client_request_reused: REQUEST_REUSED_MESSAGE,
    client_request_superseded: REQUEST_SUPERSEDED_MESSAGE,
  }),
  unprocessable: Object.freeze({ end_reason_not_available: END_REASON_UNAVAILABLE_MESSAGE }),
});

function sendContactPointCommand(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly path: string;
  readonly body: Record<string, unknown>;
  readonly messages?: CommandMessages;
  readonly send?: SendLike;
}): Promise<CommandResult> {
  return sendCommand({
    url: `${options.baseUrl}${options.path}`,
    accessToken: options.accessToken,
    body: options.body,
    messages: options.messages ?? COMMAND_MESSAGES,
    applied: (record) => ({
      channel: typeof record.channel === 'string' ? record.channel : '',
      lockVersion: typeof record.lockVersion === 'number' ? record.lockVersion : 0,
    }),
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}

export interface CommandOptions {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly send?: SendLike;
}

/**
 * `clientRequestId` ist PFLICHT und nicht optional, obwohl der Server ohne sie
 * auskommt: ein Aufruf ohne sie waere genau die Anlage, die nach einem Abbruch
 * doppelt entsteht (ADR-0060). Der Bildschirm erzeugt sie je Absenden neu und
 * haelt sie fuer die Wiederholung (client-request.ts).
 */
export function addContactPoint(
  options: CommandOptions & { readonly channel: ManagedChannel; readonly value: string; readonly clientRequestId: string },
): Promise<CommandResult> {
  return sendContactPointCommand({
    baseUrl: options.baseUrl,
    accessToken: options.accessToken,
    path: `/parties/${encodeURIComponent(options.partyId)}/contact-points`,
    body: { channel: options.channel, value: options.value, clientRequestId: options.clientRequestId },
    messages: {
      ...COMMAND_MESSAGES,
      conflicts: { ...COMMAND_MESSAGES.conflicts, party_not_writable: CONTACT_POINT_NOT_ADDABLE_MESSAGE },
    },
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}

/** "Auch verwenden als": derselbe Kontaktweg fuer einen weiteren Zweck. */
export function addContactPointUsage(
  options: CommandOptions & {
    readonly sourceContactPointUsageId: string;
    readonly usageCode: string;
    readonly clientRequestId: string;
  },
): Promise<CommandResult> {
  return sendContactPointCommand({
    baseUrl: options.baseUrl,
    accessToken: options.accessToken,
    path: `/parties/${encodeURIComponent(options.partyId)}/contact-points/${encodeURIComponent(options.sourceContactPointUsageId)}/usages`,
    body: { usageCode: options.usageCode, clientRequestId: options.clientRequestId },
    messages: {
      ...COMMAND_MESSAGES,
      conflicts: {
        ...COMMAND_MESSAGES.conflicts,
        contact_point_usage_duplicate: duplicateContactPointUsageMessage(options.usageCode),
        client_request_superseded: USAGE_SUPERSEDED_MESSAGE,
      },
    },
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}

export function endContactPoint(
  options: CommandOptions & {
    readonly contactPointUsageId: string;
    readonly endReasonCode: string;
    readonly lockVersion: number;
  },
): Promise<CommandResult> {
  return sendContactPointCommand({
    baseUrl: options.baseUrl,
    accessToken: options.accessToken,
    path: `/parties/${encodeURIComponent(options.partyId)}/contact-points/${encodeURIComponent(options.contactPointUsageId)}/end`,
    body: { endReasonCode: options.endReasonCode, lockVersion: options.lockVersion },
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}

export function preferContactPoint(
  options: CommandOptions & { readonly contactPointUsageId: string; readonly lockVersion: number },
): Promise<CommandResult> {
  return sendContactPointCommand({
    baseUrl: options.baseUrl,
    accessToken: options.accessToken,
    path: `/parties/${encodeURIComponent(options.partyId)}/contact-points/${encodeURIComponent(options.contactPointUsageId)}/preference`,
    body: { lockVersion: options.lockVersion },
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}

// --- Kontaktweg korrigieren (ST-PTY-002) -------------------------------------

/** Ausgang der Korrektur: Kanal und neuer Sperrstand des Kontaktwegs, nie der Wert. */
export type CorrectionResult = CommandOutcome<{ readonly channel: string; readonly changed: boolean; readonly contactPointLockVersion: number }>;

/** "E-Mail" bzw. "Telefonnummer" -- das Wort der Aktion und der Bestaetigung. */
export function correctionNoun(channel: string): string {
  return channel === 'phone' ? 'Telefonnummer' : 'E-Mail';
}

/** Der Feldfehler, wenn die Eingabe der bisherige Wert ist (ohne Netzaufruf). */
export const CORRECTION_UNCHANGED_HINT = 'Das ist der bisherige Wert. Ändere ihn oder brich ab.';

/** Gleicher Wert nach derselben Bereinigung wie beim Absenden (Leerraum aussen). */
export function isUnchangedCorrection(current: string, input: string): boolean {
  return current.trim() === input.trim();
}

/**
 * Der Konflikt nennt die Eingabe, damit sie nicht verloren geht: das Formular
 * schliesst, der Abschnitt laedt neu.
 */
export function correctionOutdatedMessage(input: string): PlainMessage {
  return {
    headline: 'Dieser Kontaktweg wurde inzwischen geändert.',
    explanation: 'Deine Korrektur wurde nicht gespeichert, damit die andere Änderung nicht still überschrieben wird.',
    nextStep: `Lade die Kontaktwege neu und sieh dir den jetzigen Wert an. Deine Eingabe war: ${input.trim()}`,
    retryable: false,
  };
}

export const CORRECTION_NOT_WRITABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'An diesem Kontakt lässt sich nichts mehr korrigieren.',
  explanation: 'Der Kontakt ist archiviert, anonymisiert oder mit einem anderen zusammengeführt.',
  nextStep: 'Gehe zurück zum Kontakt und prüfe seinen Stand.',
  retryable: false,
});

/**
 * Am Kontaktweg haengt eine Verwendung, die die Nutzerin nicht verantwortet.
 * Der Satz nennt keine Stelle und kein Haus (Restrisiko in ST-PTY-002), aber
 * den Ausweg: erst hinzufuegen, dann beenden -- nie ohne Weg. Die Eingabe
 * steht drin, damit sie nicht zweimal getippt wird.
 */
export function correctionNotCorrectableMessage(input: string): PlainMessage {
  return {
    headline: 'Dieser Kontaktweg lässt sich hier nicht korrigieren.',
    explanation: 'Er wird auch an einer Stelle verwendet, die du nicht ändern kannst. Gespeichert wurde nichts.',
    nextStep: `Trage den richtigen Wert mit „Kontaktweg hinzufügen“ ein und setze diesen Weg danach auf „Nicht mehr verwenden“. Deine Eingabe war: ${input.trim()}`,
    retryable: false,
  };
}

/** Abgleich nach ausgebliebener Antwort: der Kontaktweg traegt noch den alten Wert. */
export const CORRECTION_NOT_SAVED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Deine Korrektur wurde nicht gespeichert.',
  explanation: 'Die Antwort kam nicht an.',
  nextStep: 'Versuche es erneut.',
  retryable: true,
});

/** Abgleich nach ausgebliebener Antwort, und auch das Nachladen scheiterte. */
export const CORRECTION_OPEN_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Ob die Korrektur gespeichert wurde, ist offen.',
  explanation: 'Die Antwort kam nicht an.',
  nextStep: 'Lade die Kontaktwege neu. Dann siehst du, was gilt.',
  retryable: true,
});

/** Die Bestaetigung nennt den neuen Wert; bei mehreren Zwecken auch, dass sie alle gelten. */
export function correctionConfirmation(channel: string, value: string, purposes: number): string {
  const satz = `${correctionNoun(channel)} korrigiert: ${value.trim()}.`;
  return purposes > 1 ? `${satz} Die Korrektur gilt für alle Zwecke.` : satz;
}

/**
 * Wie die Sperre "Nicht zustellbar" nach der Korrektur steht (ST-PTY-002,
 * decisions): aufgehoben, bewusst weiter gueltig (Haekchen
 * abgewaehlt), nicht aufgehoben (Teilerfolg) oder offen (keine Antwort und
 * kein Abgleich). Ohne Sperre am Weg: `keine`.
 */
export type CorrectionSuppressionOutcome = 'keine' | 'aufgehoben' | 'gilt-weiter' | 'nicht-aufgehoben' | 'offen';

/** Das Haekchen im Formular; der Klammerteil ist das Wort des Aufhebegrunds contact_corrected. */
export const CORRECTION_LIFT_LABEL = 'Sperre „Nicht zustellbar“ ebenfalls aufheben (Angabe korrigiert)';

/** Der Hinweis im Formular je Sperre am Weg; mit Haekchen sagt dieses, dass sie sonst weiter gilt. */
export function correctionSuppressionNote(reasonWord: string, mitHaekchen: boolean): string {
  const satz = `Für diesen Weg gilt die Sperre „${reasonWord}“.`;
  return mitHaekchen ? satz : `${satz} Sie gilt auch nach der Korrektur.`;
}

/** Die Bestaetigung samt Sperrsatz; Teilerfolg und offen stehen in der Warnleiste (correctionSuppressionWarning). */
export function correctionConfirmationWith(channel: string, value: string, purposes: number, sperre: CorrectionSuppressionOutcome): string {
  const satz = correctionConfirmation(channel, value, purposes);
  if (sperre === 'aufgehoben') return `${satz} Die Sperre „Nicht zustellbar“ ist aufgehoben.`;
  if (sperre === 'gilt-weiter') return `${satz} Die Sperre „Nicht zustellbar“ gilt weiter.`;
  return satz;
}

/**
 * Die Warnleiste, wenn die Korrektur gespeichert ist, das Aufheben aber nicht
 * (oder offen). Sie nennt die Korrektur als gespeichert -- nie "nichts
 * gespeichert" (Teilerfolg). `vorLink`/`nachLink` umschliessen den Link
 * "Sperren".
 */
export function correctionSuppressionWarning(
  channel: string,
  value: string,
  sperre: 'nicht-aufgehoben' | 'offen',
): { readonly titel: string; readonly erklaerung: string; readonly vorLink: string; readonly nachLink: string } {
  return {
    titel: `${correctionNoun(channel)} korrigiert: ${value.trim()}.`,
    erklaerung:
      sperre === 'offen'
        ? 'Ob die Sperre aufgehoben wurde, ist offen.'
        : 'Die Sperre „Nicht zustellbar“ ließ sich nicht aufheben und gilt weiter.',
    vorLink: sperre === 'offen' ? 'Sieh unter ' : 'Hebe sie unter ',
    nachLink: sperre === 'offen' ? ' nach, was gilt.' : ' auf.',
  };
}

/** Ein Gateway ohne Antwort des Dienstes ist fuer den Abgleich wie ein Netzabbruch. */
const GATEWAY_WITHOUT_ANSWER: ReadonlySet<number> = new Set([502, 504]);

export class CorrectionNoAnswerError extends Error {}

export function correctContactPoint(
  options: CommandOptions & { readonly contactPointUsageId: string; readonly value: string; readonly contactPointLockVersion: number },
): Promise<CorrectionResult> {
  const send = options.send ?? fetch;
  return sendCommand({
    url: `${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/contact-points/${encodeURIComponent(options.contactPointUsageId)}/correction`,
    accessToken: options.accessToken,
    body: { value: options.value.trim(), contactPointLockVersion: options.contactPointLockVersion },
    messages: {
      ...COMMAND_MESSAGES,
      conflicts: {
        ...COMMAND_MESSAGES.conflicts,
        contact_point_outdated: correctionOutdatedMessage(options.value),
        contact_point_not_correctable: correctionNotCorrectableMessage(options.value),
        party_not_writable: CORRECTION_NOT_WRITABLE_MESSAGE,
      },
    },
    applied: (record) => ({
      channel: typeof record.channel === 'string' ? record.channel : '',
      changed: record.changed === true,
      contactPointLockVersion: typeof record.contactPointLockVersion === 'number' ? record.contactPointLockVersion : 0,
    }),
    send: async (url, init) => {
      const response = await send(url, init);
      if (GATEWAY_WITHOUT_ANSWER.has(response.status)) throw new CorrectionNoAnswerError(`Gateway ${response.status}`);
      return response;
    },
  });
}

/**
 * Die Bestaetigung nach einem geglueckten Befehl.
 *
 * SIE NENNT DIE WIRKUNG UND NICHT DEN VORGANG. "Gespeichert" sagt nichts;
 * "Die E-Mail-Adresse ist beendet. Sie steht jetzt unter Frühere Kontaktwege."
 * sagt, was sich geaendert hat und wo es zu finden ist -- CM-UX-004 verlangt
 * fuer eine folgenreiche Aenderung ausdruecklich Wirkung UND Rueckweg.
 */
export function confirmationFor(operation: 'add' | 'end' | 'prefer', channel: string): string {
  const kanal = channelLabel(channel);
  if (operation === 'add') {
    return `${kanal} hinzugefügt. Der Kontaktweg gilt ab sofort.`;
  }
  if (operation === 'end') {
    return `${kanal} wird nicht mehr verwendet. Der Kontaktweg steht jetzt unter „Frühere Kontaktwege“ und ist dort weiter nachlesbar.`;
  }
  return `${kanal} ist jetzt der bevorzugte Kontaktweg. Der bisher bevorzugte gilt weiter, ist aber nicht mehr bevorzugt.`;
}

/**
 * Der Hinweis unter einer abgeschnittenen Historie.
 *
 * Er nennt KEINE Zahl. Der Server liefert keine, und eine geschaetzte waere
 * eine Auskunft ueber einen Bestand, den diese Antwort nicht kennt.
 */
export const HISTORY_TRUNCATED_NOTICE =
  'Es gibt weitere frühere Kontaktwege, die diese Ansicht nicht zeigt.';

/**
 * mailto: für genau die angezeigte Adresse. Der lokale Teil wird vollständig
 * kodiert: ein Komma trennte sonst Empfänger, ? & # setzten Parameter (cc,
 * subject). Die Domain trägt nur Buchstaben, Ziffern, Punkt und Strich.
 */
export function mailtoAdresse(wert: string): string {
  const at = wert.lastIndexOf('@');
  if (at < 0) return `mailto:${encodeURIComponent(wert)}`;
  return `mailto:${encodeURIComponent(wert.slice(0, at))}@${encodeURIComponent(wert.slice(at + 1))}`;
}

/** tel: aus Ziffern mit führendem +; Leerzeichen, Klammern und Striche fallen weg, die Ziffern einer Durchwahl bleiben. */
export function telAdresse(wert: string): string {
  return `tel:${wert.trim().startsWith('+') ? '+' : ''}${wert.replace(/\D/g, '')}`;
}

/**
 * Der Link einer Website (Signatur S2, X2). Verlinkt wird nur die Form, die der
 * Server speichert (Host mit Punkt, optional Pfad, ohne Schema); alles andere
 * bleibt Text, damit kein fremdes Schema wie javascript: in den Link gelangt.
 */
export function websiteHref(wert: string): string | undefined {
  return /^(?:[\p{L}\p{N}-]+\.)+[\p{L}]{2,}(?:\/[^\s]*)?$/u.test(wert) ? `https://${wert}` : undefined;
}
